import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { genres as allGenres, regions as allRegions, years as allYears } from "@/mocks/filters";
import { filterMovies, getByType, sortMovies, type SortKey } from "@/utils/movie";
import { usePageMeta } from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import MultiSelect from "@/components/base/MultiSelect";
import Pagination from "@/components/base/Pagination";
import FilterDrawer from "./components/FilterDrawer";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "time", label: "更新时间" },
  { key: "views", label: "热度" },
  { key: "rating", label: "评分" },
];

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const categoryType = type === "tv" ? "tv" : "movie";
  const label = categoryType === "movie" ? "电影" : "电视剧";

  usePageMeta(
    `${label} - 秋霞网影视资源`,
    `秋霞网为您提供${label}最新最全影视资源，支持筛选与观看辅助。`
  );

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("time");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setSort("time");
    setPage(1);
  }, [categoryType]);

  useEffect(() => {
    setPage(1);
  }, [selectedGenres, selectedRegions, selectedYears, sort]);

  const baseList = useMemo(() => getByType(categoryType), [categoryType]);

  const filtered = useMemo(
    () =>
      sortMovies(
        filterMovies(baseList, {
          genres: selectedGenres,
          regions: selectedRegions,
          years: selectedYears.map(Number),
        }),
        sort
      ),
    [baseList, selectedGenres, selectedRegions, selectedYears, sort]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasFilter =
    selectedGenres.length + selectedRegions.length + selectedYears.length > 0;

  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
  };

  const toggleIn = (
    value: string,
    list: string[],
    setter: (v: string[]) => void
  ) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const filterCount =
    selectedGenres.length + selectedRegions.length + selectedYears.length;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-500 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">{label}</span>
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {label}频道
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          秋霞网为您提供{label}最新最全影视资源，支持类型、地区、年份多维筛选与排序。
        </p>
      </header>

      <div className="mt-6 rounded-xl border border-background-300/60 bg-background-100 p-4">
        <div className="hidden flex-wrap items-center gap-3 md:flex">
          <MultiSelect
            label="类型"
            options={allGenres.map((g) => ({ label: g, value: g }))}
            selected={selectedGenres}
            onChange={setSelectedGenres}
          />
          <MultiSelect
            label="地区"
            options={allRegions.map((r) => ({ label: r, value: r }))}
            selected={selectedRegions}
            onChange={setSelectedRegions}
          />
          <MultiSelect
            label="年份"
            options={allYears.map((y) => ({ label: `${y}年`, value: String(y) }))}
            selected={selectedYears}
            onChange={setSelectedYears}
          />
          {hasFilter && (
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-500 transition-colors hover:text-primary-500 cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置
            </button>
          )}

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-foreground-500">排序</span>
            <div className="flex items-center gap-1 rounded-full border border-background-300 p-1">
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSort(s.key)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors cursor-pointer ${
                    sort === s.key
                      ? "bg-primary-500 text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 md:hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-900 cursor-pointer"
          >
            <i className="ri-filter-3-line" />
            筛选排序
            {filterCount > 0 && (
              <span className="rounded-full bg-primary-500 px-1.5 text-[10px] font-medium text-foreground-950">
                {filterCount}
              </span>
            )}
          </button>
          <span className="text-xs text-foreground-500">
            共 {filtered.length} 部
          </span>
        </div>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {pageItems.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          <Pagination current={page} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-background-300/60 bg-background-100 px-6 py-20 text-center">
          <i className="ri-search-eye-line text-5xl text-foreground-400" />
          <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
            暂无符合条件的影视资源
          </h3>
          <p className="mt-2 text-sm text-foreground-500">
            试试减少筛选条件，或重置后重新浏览
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            重置筛选
          </button>
        </div>
      )}

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedGenres={selectedGenres}
        selectedRegions={selectedRegions}
        selectedYears={selectedYears}
        onToggleGenre={(v) => toggleIn(v, selectedGenres, setSelectedGenres)}
        onToggleRegion={(v) => toggleIn(v, selectedRegions, setSelectedRegions)}
        onToggleYear={(v) => toggleIn(v, selectedYears, setSelectedYears)}
        onReset={resetFilters}
        sort={sort}
        onSortChange={setSort}
      />
    </div>
  );
}